/**
 * Adapter interface and production implementation for the @entrata/i18n global.
 *
 * DIP: all formatting providers depend on I18nAdapter (abstraction), not on
 * the i18nUtils global (concretion). The global is accessed in exactly one
 * place — globalI18nAdapter — making it trivial to swap in a test double.
 *
 * SRP: the sole responsibility of this module is translating between the
 * i18nUtils global API and the typed interface the rest of the app uses.
 */

type PhoneFormatResult = {
  formattedPhoneNumber: string;
  formattedNumber: RegExp;
  isValid: boolean;
};

export type PhoneFormatter = (digits: string, countryCode: number) => PhoneFormatResult | false;

export type PhoneUtils = {
  getPhonePlaceholder?: () => string;
};

export type DateUtils = {
  getPlaceholder?: () => string;
  getRawValue?: (value: string) => string;
  pipeFormat?: string;
};

/**
 * Abstraction over the i18nUtils global. Methods return undefined when the
 * underlying capability is unavailable, rather than requiring every caller
 * to guard with hasI18nUtils() / typeof checks.
 */
export interface I18nAdapter {
  getLocale(): string | undefined;
  currency(value: number, options?: string): string | undefined;
  number(value: number | string, options?: string): string | undefined;
  datetime(value: Date | string, format?: string): string | undefined;
  getCountryCode(): number | undefined;
  isValidCountryCode(countryCode: number): boolean;
  getPhoneFormat(): PhoneFormatter | undefined;
  phone(value: string): string | undefined;
  getPhoneUtils(localeOrCode?: string | number): PhoneUtils | undefined;
  getDateUtils(localeOrConfig?: string | { locale: string }): DateUtils | undefined;
}

function isAvailable(): boolean {
  return typeof i18nUtils !== 'undefined';
}

/**
 * Production adapter — all i18nUtils global access is contained here.
 * Swap this out in tests via createDefaultFormattingRegistry(mockAdapter).
 */
export const globalI18nAdapter: I18nAdapter = {
  getLocale: () =>
    isAvailable() && typeof i18nUtils.getLocale === 'function'
      ? i18nUtils.getLocale() || undefined
      : undefined,

  currency: (value, options?) =>
    isAvailable() && typeof i18nUtils.currency === 'function'
      ? i18nUtils.currency(value, options)
      : undefined,

  number: (value, options?) =>
    isAvailable() && typeof i18nUtils.number === 'function'
      ? i18nUtils.number(value, options)
      : undefined,

  datetime: (value, format?) =>
    isAvailable() && typeof i18nUtils.datetime === 'function'
      ? i18nUtils.datetime(value, format)
      : undefined,

  getCountryCode: () =>
    isAvailable() && typeof i18nUtils.getCountryCode === 'function'
      ? i18nUtils.getCountryCode()
      : undefined,

  isValidCountryCode: (code) =>
    isAvailable() && typeof i18nUtils.isValidCountryCode === 'function'
      ? i18nUtils.isValidCountryCode(code)
      : true,

  getPhoneFormat: () =>
    isAvailable() && typeof i18nUtils.getPhoneFormat === 'function'
      ? i18nUtils.getPhoneFormat()
      : undefined,

  phone: (value) =>
    isAvailable() && typeof i18nUtils.phone === 'function' ? i18nUtils.phone(value) : undefined,

  getPhoneUtils: (localeOrCode?) =>
    isAvailable() && typeof i18nUtils.getPhoneUtils === 'function'
      ? i18nUtils.getPhoneUtils(localeOrCode)
      : undefined,

  getDateUtils: (localeOrConfig?) =>
    isAvailable() && typeof i18nUtils.getDateUtils === 'function'
      ? i18nUtils.getDateUtils(localeOrConfig)
      : undefined,
};
